/** Info-subtlest preview card rendered inside the refund drawer. */
import { tokens } from "@blurb/codex-react";
import { createStyles, Loader } from "@mantine/core";
import { GraphQL, isGraphQLError } from "@rpi/openapi-api";
import React from "react";
import { Body } from "../../../../components/Typography";
import { formatCurrencyV2 } from "../../../../utils/misc.util";
import type { OrderItem } from "../../OrderItemSelector";
import type { RefundItemInputType } from "./refund.constants";
import type { OrderForRefund } from "./refund.utils";
import type { RefundPreviewResult } from "./useRefundPreview";

const useStyles = createStyles({
  card: {
    width: "100%",
    backgroundColor: tokens.color.semantic.bg.infoSubtlest,
    borderRadius: tokens.radius.md,
    padding: tokens.spacing[4],
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacing[4],
  },
  itemsSection: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacing[2],
  },
  itemBlock: {
    display: "flex",
    flexDirection: "column",
  },
  itemAmountRow: {
    // Grid keeps the unit price, quantity marker, and refund amount aligned
    // across multiple line items even when their content widths differ.
    display: "grid",
    gridTemplateColumns: "minmax(60px, auto) minmax(40px, auto) 1fr",
    gap: tokens.spacing[3],
    paddingLeft: tokens.spacing[4],
    alignItems: "baseline",
  },
  // The Partial Refund ($) strategy has no `x N` middle column — the user
  // entered an explicit amount per item, so the multiplier would be redundant.
  itemAmountRowTwoCol: {
    gridTemplateColumns: "minmax(60px, auto) 1fr",
  },
  itemRefundAmount: {
    textAlign: "right" as const,
  },
  totalsSection: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacing[2],
  },
  totalsRow: {
    display: "flex",
    alignItems: "baseline",
  },
  totalsRowValue: {
    flex: 1,
    textAlign: "right" as const,
  },
  dividerFaint: {
    height: 1,
    width: "100%",
    backgroundColor: tokens.color.semantic.border.subtle,
  },
  dividerBold: {
    height: 1,
    width: "100%",
    backgroundColor: tokens.color.semantic.icon.bold,
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    padding: tokens.spacing[4],
  },
});

interface RefundPreviewProps {
  preview: RefundPreviewResult;
  order: OrderForRefund;
  /** Selects how the per-line middle column renders (e.g., `x 2` vs `x 10%`). */
  inputType: RefundItemInputType;
  /** Raw user-typed values keyed by `OrderItem.id`, used as the
   *  source of truth for the per-line multiplier display. */
  userInputByItemId: Map<number, string>;
}

export function RefundPreview({ preview, order, inputType, userInputByItemId }: RefundPreviewProps) {
  const { classes } = useStyles();
  const currency = order.currency ?? GraphQL.SupportedCurrency.Usd;

  if (preview.error) {
    return (
      <div className={classes.card}>
        <Body size="sm">
          {isGraphQLError(preview.error) ? preview.error.message : "Unable to calculate refund preview."}
        </Body>
      </div>
    );
  }

  if (!preview.data) {
    // With `keepPreviousData`, `!data` only persists while the first query is
    // in-flight. If nothing is fetching either, the hook is idle (no request)
    // and there's nothing to show — drawer-level rendering normally prevents
    // this, but guard defensively in case the drawer renders us prematurely.
    if (!preview.isFetching) return null;
    return (
      <div className={classes.card}>
        <div className={classes.loading}>
          <Loader color="brand" size="sm" />
        </div>
      </div>
    );
  }

  const { lineItems, refundSubtotal, refundTax, totalRefund } = preview.data;
  const orderItemById = buildOrderItemLookup(order);

  return (
    <div className={classes.card} aria-busy={preview.isFetching}>
      <Body size="lg" weight="bold">
        Refund Summary
      </Body>
      <div className={classes.dividerFaint} />

      {lineItems.length > 0 && (
        <>
          <div className={classes.itemsSection}>
            {lineItems.map((line) => {
              const row = resolveLineItemRow(line, orderItemById, userInputByItemId);
              return (
                <LineItemRow
                  key={row.key}
                  label={row.label}
                  unitPrice={line.unitPrice}
                  quantity={line.quantity}
                  refundAmount={line.refundAmount}
                  currency={currency}
                  inputType={inputType}
                  userValue={row.userValue}
                  classes={classes}
                />
              );
            })}
          </div>
          <div className={classes.dividerBold} />
        </>
      )}

      <div className={classes.totalsSection}>
        <div className={classes.totalsRow}>
          <Body size="base" weight="semibold">
            Subtotal
          </Body>
          <Body size="base" weight="semibold" className={classes.totalsRowValue}>
            {formatCurrencyV2(currency, refundSubtotal)}
          </Body>
        </div>
        <div className={classes.totalsRow}>
          <Body size="base">{formatTaxLabel(refundTax, refundSubtotal)}</Body>
          <Body size="base" className={classes.totalsRowValue}>
            {formatCurrencyV2(currency, refundTax)}
          </Body>
        </div>
      </div>

      <div className={classes.dividerBold} />

      <div className={classes.totalsRow}>
        <Body size="base" weight="semibold">
          Total Refund Amount
        </Body>
        <Body size="base" weight="semibold" className={classes.totalsRowValue}>
          {formatCurrencyV2(currency, totalRefund)}
        </Body>
      </div>
    </div>
  );
}

interface LineItemRowProps {
  label: string;
  unitPrice: number;
  quantity: number;
  refundAmount: number;
  currency: GraphQL.SupportedCurrency;
  inputType: RefundItemInputType;
  userValue: string | undefined;
  classes: {
    itemBlock: string;
    itemAmountRow: string;
    itemAmountRowTwoCol: string;
    itemRefundAmount: string;
  };
}

function LineItemRow({
  label,
  unitPrice,
  quantity,
  refundAmount,
  currency,
  inputType,
  userValue,
  classes,
}: LineItemRowProps) {
  const multiplier = formatLineMultiplier(inputType, quantity, userValue);
  const amountRowClass =
    multiplier === undefined ? `${classes.itemAmountRow} ${classes.itemAmountRowTwoCol}` : classes.itemAmountRow;
  return (
    <div className={classes.itemBlock}>
      <Body size="base">{label}</Body>
      <div className={amountRowClass}>
        <Body size="base">{formatCurrencyV2(currency, unitPrice)}</Body>
        {multiplier !== undefined && <Body size="base">{multiplier}</Body>}
        <Body size="base" className={classes.itemRefundAmount}>
          {formatCurrencyV2(currency, refundAmount)}
        </Body>
      </div>
    </div>
  );
}

/**
 * Returns the middle-column text for a line item row, or `undefined` when
 * the strategy has no meaningful multiplier (the `$` strategy already encodes
 * the per-item amount in the right-hand refund cell). The user's typed value
 * is the source of truth so the summary matches what the user is about to
 * submit, without depending on the backend echoing fields in a specific shape.
 */
function formatLineMultiplier(
  inputType: RefundItemInputType,
  quantity: number,
  userValue: string | undefined
): string | undefined {
  if (inputType === "amount") return undefined;
  if (inputType === "percentage") {
    return userValue ? `x ${userValue}%` : "";
  }
  return `x ${quantity}`;
}

function formatTaxLabel(refundTax: number, refundSubtotal: number): string {
  if (refundSubtotal <= 0) return "Taxes";
  const percent = (refundTax / refundSubtotal) * 100;
  return `Taxes (${percent.toFixed(2)}%)`;
}

function buildOrderItemLookup(order: OrderForRefund): Map<number, OrderItem> {
  const lookup = new Map<number, OrderItem>();
  for (const item of order.orderItems ?? []) {
    lookup.set(item.id, item);
  }
  return lookup;
}

function buildItemLabel(item: OrderItem): string {
  const name = item.openapiProduct?.name ?? item.customerProduct?.name ?? "Item";
  return `${item.id} - ${name}`;
}

function resolveLineItemRow(
  line: { id: number },
  orderItemLookup: Map<number, OrderItem>,
  userInputByItemId: Map<number, string>
): { key: string; label: string; userValue: string | undefined } {
  const orderItem = orderItemLookup.get(line.id);
  return {
    key: `orderItem:${line.id}`,
    label: orderItem ? buildItemLabel(orderItem) : `Item ${line.id}`,
    userValue: userInputByItemId.get(line.id),
  };
}
